import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { usePeriod } from "@/context/PeriodContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, ClipboardCheck, Lock } from "lucide-react";

const CHECKS = [
  { key: "revenue_entered", tr: "Günlük ciro girildi",   de: "Tagesumsatz erfasst" },
  { key: "expenses_entered", tr: "Giderler girildi",     de: "Ausgaben erfasst" },
  { key: "cash_counted",    tr: "Kasa sayıldı",          de: "Kasse gezählt" },
  { key: "docs_missing",    tr: "Eksik belge var mı?",   de: "Fehlende Belege?" },
];

const todayISO = () => new Date().toISOString().slice(0, 10);

export default function DailyCloseCard() {
  const { lang } = useI18n();
  const { isSelectedClosed } = usePeriod();
  const [today] = useState(todayISO());
  const [checklist, setChecklist] = useState({
    revenue_entered: false, expenses_entered: false, cash_counted: false, docs_missing: false,
  });
  const [notes, setNotes] = useState("");
  const [closedAt, setClosedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/daily-close?date=${today}`);
        setChecklist({ ...checklist, ...(data?.checklist || {}) });
        setNotes(data?.notes || "");
        setClosedAt(data?.closed_at || null);
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [today]);

  const t = (tr, de) => (lang === "de" ? de : tr);
  const readOnly = isSelectedClosed || !!closedAt;

  const save = async (close = false) => {
    setSaving(true);
    try {
      const { data } = await api.post("/daily-close", { date: today, checklist, notes, closed: close });
      setClosedAt(data?.closed_at || null);
      toast.success(close ? t("Gün kapatıldı", "Tag geschlossen") : t("Kaydedildi", "Gespeichert"));
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (msg?.message || t("Kaydedilemedi", "Speichern fehlgeschlagen")));
    } finally {
      setSaving(false);
    }
  };

  const doneCount = Object.entries(checklist).filter(([k, v]) => (k === "docs_missing" ? !v : !!v)).length;

  return (
    <div className="rounded-xl border bg-background p-4 space-y-3" data-testid="daily-close-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ClipboardCheck className="w-4 h-4" />
          <div className="text-sm font-semibold">{t("Günlük Kapanış", "Tagesabschluss")}</div>
          <span className="text-xs font-mono text-muted-foreground">{today}</span>
        </div>
        <div className="flex items-center gap-2">
          {closedAt && (
            <Badge className="bg-emerald-600 hover:bg-emerald-600 gap-1" data-testid="dc-closed-badge">
              <CheckCircle2 className="w-3 h-3" /> {t("Kapandı", "Geschlossen")}
            </Badge>
          )}
          {isSelectedClosed && (
            <Badge variant="outline" className="border-slate-300 text-slate-600 gap-1">
              <Lock className="w-3 h-3" /> {t("Kapalı Ay", "Geschlossen")}
            </Badge>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {CHECKS.map((c) => (
          <label key={c.key} className="flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer hover:bg-muted/40 select-none">
            <Checkbox
              checked={!!checklist[c.key]}
              onCheckedChange={(v) => setChecklist((prev) => ({ ...prev, [c.key]: !!v }))}
              disabled={readOnly}
              data-testid={`dc-${c.key}`}
            />
            <span className="text-sm">{lang === "de" ? c.de : c.tr}</span>
          </label>
        ))}
      </div>

      <Textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder={t("Notlar (opsiyonel)", "Notizen (optional)")}
        disabled={readOnly}
        data-testid="dc-notes"
        className="min-h-[60px]"
      />

      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {doneCount}/4 · {closedAt ? t("Kapatıldı", "Geschlossen") + " · " + closedAt.slice(0, 16).replace("T", " ") : t("Bekliyor", "Ausstehend")}
        </span>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => save(false)} disabled={saving || readOnly} data-testid="dc-save-btn">
            {t("Kaydet", "Speichern")}
          </Button>
          <Button size="sm" onClick={() => save(true)} disabled={saving || readOnly || !!closedAt} data-testid="dc-close-btn">
            {t("Bugünü Kapat", "Tag schließen")}
          </Button>
        </div>
      </div>
    </div>
  );
}
